import * as THREE from 'three';
import type { World } from '../../shared/types.js';
import type { Skyline } from './outroCamera.js';

/**
 * A slow circle around a whole route, high and wide: the city behind the menu.
 *
 * It was the pre-race establishing shot until `FlyoverCamera` took that over (DECISIONS.md
 * §68): on a long route, standing back far enough to frame all of it put the edge of the
 * generated world in the frame. Behind the menu that does not matter — the sheet on top is
 * opaque and the backdrop is vignetted — so it stayed there. What follows is why it was
 * built, which is still why it is shaped the way it is.
 *
 * This is the answer to a real problem with a game that builds its track out of a place
 * you have never been: at the moment the lights go out you are looking down a street with
 * no idea whether the route turns left in fifty metres or runs straight for a kilometre.
 * The outro camera (`outroCamera.ts`) circles a *car* at 46 m. This one circles the
 * *region*, high and wide enough to take in the whole route at once, so by the time the
 * countdown starts you have seen where you are going.
 *
 * It is a sibling of `OutroCamera` rather than a mode of it. They share the shape of the
 * arithmetic and almost nothing else: that one tracks a moving subject and has to survive
 * the subject changing mid-shot, this one frames a fixed rectangle and knows the whole
 * flight in advance.
 */

/**
 * Degrees a second. Slower than the outro's 11°/s, because the subject is a square
 * kilometre rather than a car: the same angular rate over a 400 m radius is a camera
 * travelling at 270 km/h, and the city smears.
 */
const ORBIT_SPEED = 6.5;

/**
 * How much wider than the route the orbit sits, and how high relative to that.
 *
 * 1.55 was picked by watching all four fixtures: below about 1.4 the ends of Manhattan's
 * route leave the frame on every pass, and above about 1.8 Kraków's Old Town — the
 * smallest of them — becomes a texture rather than a place.
 */
const FRAMING = 1.55;
const HEIGHT_RATIO = 0.62;

/** Never closer than this, or a tiny region puts the camera inside a building. */
const MIN_RADIUS = 130;
/** Metres of air above the tallest roof under the camera's path. */
const ROOF_CLEARANCE = 40;

/**
 * The shot descends over its first stretch: it opens high and wide and settles to about
 * two-thirds of that. A locked-off orbit at a fixed height reads as a menu background —
 * the slow fall is most of what makes it read as a camera someone is flying.
 */
const DESCENT_SECONDS = 14;
const DESCENT_TO = 0.66;

/** Wider than the chase camera's 55°, to take in a whole district. */
const ORBIT_FOV = 60;

const LOOK_LIFT = 0.12;

export class OrbitCamera {
  private readonly focus = new THREE.Vector3();
  private readonly desired = new THREE.Vector3();
  private centre = { x: 0, z: 0 };
  private radius = MIN_RADIUS;
  private angle = 0;
  private elapsed = 0;
  private skyline: Skyline | null = null;
  private active = false;

  /**
   * Frames the route, not the region.
   *
   * A bounding box drawn on the map is mostly city nobody will drive through; a 4 km²
   * region with a 600 m route through one corner of it should put the camera over the
   * route. The region's own extent is only used as a ceiling, so the shot can never end up
   * framing ground the world does not contain.
   */
  setWorld(world: World | null, skyline: Skyline | null): void {
    this.skyline = skyline;
    if (!world) return;

    const path = world.route.path;
    let minX = Infinity;
    let maxX = -Infinity;
    let minZ = Infinity;
    let maxZ = -Infinity;
    for (let i = 0; i < path.length; i += 2) {
      minX = Math.min(minX, path[i]);
      maxX = Math.max(maxX, path[i]);
      minZ = Math.min(minZ, path[i + 1]);
      maxZ = Math.max(maxZ, path[i + 1]);
    }
    if (!Number.isFinite(minX)) {
      minX = maxX = world.route.start.x;
      minZ = maxZ = world.route.start.z;
    }

    this.centre = { x: (minX + maxX) / 2, z: (minZ + maxZ) / 2 };
    const span = Math.max(maxX - minX, maxZ - minZ) / 2;
    const ceiling = Math.max(world.extent.x, world.extent.z) * 1.35;
    this.radius = THREE.MathUtils.clamp(span * FRAMING, MIN_RADIUS, Math.max(MIN_RADIUS, ceiling));

    // Open from behind the start line, looking the way the race will go. Anywhere else and
    // the first thing the shot says about the route is where it ends.
    const start = world.route.start;
    this.angle = Math.atan2(-start.dx, -start.dz);
  }

  /** Begins the shot. `elapsed` restarts so every race opens on the same wide frame. */
  reset(): void {
    this.active = true;
    this.elapsed = 0;
    this.focus.set(this.centre.x, 0, this.centre.z);
    // Placed exactly on the opening pose rather than eased into it: there is nothing on
    // screen to cut away from, so easing in from wherever the camera was left after the
    // last race just looks like a lurch.
    this.place(this.pose(0), 1);
  }

  stop(): void {
    this.active = false;
  }

  get running(): boolean {
    return this.active;
  }

  update(camera: THREE.PerspectiveCamera, dt: number): void {
    if (!this.active) return;
    this.elapsed += dt;
    this.angle += THREE.MathUtils.degToRad(ORBIT_SPEED) * dt;

    const pose = this.pose(this.elapsed);
    this.place(pose, 1 - Math.exp(-3 * dt));

    camera.position.copy(this.desired);
    camera.lookAt(this.focus.x, pose.height * LOOK_LIFT, this.focus.z);
    if (camera.fov !== ORBIT_FOV) {
      camera.fov = ORBIT_FOV;
      camera.updateProjectionMatrix();
    }
  }

  private pose(elapsed: number): { radius: number; height: number } {
    // `smoothstep` rather than a straight ramp so the descent has no corner at either end.
    const t = THREE.MathUtils.clamp(elapsed / DESCENT_SECONDS, 0, 1);
    const eased = t * t * (3 - 2 * t);
    const scale = 1 - (1 - DESCENT_TO) * eased;
    const radius = this.radius * scale;
    let height = radius * HEIGHT_RATIO;
    // Sampled where the camera is going to be, not where it is: a tower the shot is about
    // to swing past is the one it needs to clear, and by the time it is overhead it is too
    // late to climb.
    const ahead = this.angle + THREE.MathUtils.degToRad(ORBIT_SPEED) * 1.5;
    const roof = this.skyline?.heightAt(
      this.focus.x + Math.sin(ahead) * radius,
      this.focus.z + Math.cos(ahead) * radius,
    );
    if (roof) height = Math.max(height, roof + ROOF_CLEARANCE);
    return { radius, height };
  }

  private place(pose: { radius: number; height: number }, k: number): void {
    const target = new THREE.Vector3(
      this.centre.x + Math.sin(this.angle) * pose.radius,
      pose.height,
      this.centre.z + Math.cos(this.angle) * pose.radius,
    );
    this.focus.x += (this.centre.x - this.focus.x) * k;
    this.focus.z += (this.centre.z - this.focus.z) * k;
    if (k >= 1) this.desired.copy(target);
    else this.desired.lerp(target, k);
  }
}
